import { GIFEncoder, quantize, applyPalette } from 'gifenc';

export interface TimelapseEncodeOptions {
    /** Delay between frames in milliseconds. */
    delayMs?: number;
    /** Scale output so width does not exceed this (keeps GIF size reasonable). */
    maxWidth?: number;
    /** Draw a semi-transparent Empire Maps watermark (free tier). */
    watermark?: boolean;
}

async function pngBlobToRgba(
    blob: Blob,
    maxWidth: number,
    watermark: boolean,
): Promise<{ rgba: Uint8ClampedArray; width: number; height: number }> {
    const bitmap = await createImageBitmap(blob);
    const scale = Math.min(1, maxWidth / bitmap.width);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        bitmap.close();
        throw new Error('Could not rasterize timelapse frame');
    }

    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    if (watermark) {
        const label = 'EmpireMap.live';
        const pad = Math.max(10, Math.round(width * 0.02));
        ctx.font = `800 ${Math.max(14, Math.round(width * 0.028))}px -apple-system, "Segoe UI", sans-serif`;
        const textW = ctx.measureText(label).width;
        const boxW = textW + pad * 2;
        const boxH = Math.max(28, Math.round(width * 0.045));
        const x = width - boxW - pad;
        const y = height - boxH - pad;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
        ctx.fillRect(x, y, boxW, boxH);
        ctx.fillStyle = 'rgba(249, 115, 22, 0.95)';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, x + pad, y + boxH / 2);
    }

    const { data } = ctx.getImageData(0, 0, width, height);
    return { rgba: data, width, height };
}

/** Stitch branded PNG snapshots into an animated GIF. */
export async function encodeGifFromPngBlobs(
    frames: Blob[],
    options: TimelapseEncodeOptions = {},
): Promise<Blob> {
    const { delayMs = 450, maxWidth = 720, watermark = false } = options;
    if (frames.length === 0) {
        throw new Error('No frames to encode');
    }

    const gif = GIFEncoder();
    let width = 0;
    let height = 0;

    for (const frame of frames) {
        const { rgba, width: w, height: h } = await pngBlobToRgba(frame, maxWidth, watermark);
        width = w;
        height = h;
        const palette = quantize(rgba, 256);
        const index = applyPalette(rgba, palette);
        gif.writeFrame(index, w, h, { palette, delay: delayMs });
    }

    gif.finish();
    const bytes = gif.bytes();
    return new Blob([bytes], { type: 'image/gif' });
}

export function downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
}
